/* global google */
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Button, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDrawPolygon, faMousePointer, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { MAP } from "react-google-maps/lib/constants";
import {
  createTerraDraw, DRAW_MODE, SELECT_MODE, STATIC_MODE} from "../utils/terraDrawConfig";
import Colors from "../../../../themes/Colors";

const ButtonGroup = Button.Group;

const featureToPolygon = (feature) => {
  const ring = feature.geometry.coordinates[0];
  if (ring.length < 3) return null;
  const path = ring.map((point) => new google.maps.LatLng(point[1], point[0]));
  return new google.maps.Polygon({ paths: path });
};

const isInsidePolygon = (display, polygon) =>
  google.maps.geometry.poly.containsLocation(
    new google.maps.LatLng(display.latitude, display.longitude),
    polygon
  );

const collectDisplaysInside = (drawnPolygon, displaysMap, cart) =>
  (displaysMap || []).filter((display) => {
    if (cart.find((d) => d.id === display.id)) return false;
    return isInsidePolygon(display, drawnPolygon);
  });

// Map control that toggles polyline drawing
const DrawControlButton = ({
  map,
  displaysMap,
  cart,
  addDisplaysFromPolygon,
  updateArrayPolygon,
  removeDisplaysFromPolygon,
  shouldClearPolygons,
  onPolygonsCleared,
  hidden,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(STATIC_MODE);
  // Id of the area picked in select mode, or null. Every path that ends a
  // selection — picking another one, `Supr`, removeFeatures, leaving the mode —
  // goes through the mode's deselect event, so this needs no manual clearing.
  const [selectedId, setSelectedId] = useState(null);
  const [container, setContainer] = useState(null);
  const terraDrawRef = useRef(null);
  const areasRef = useRef(new Map());
  // Set while areas are removed programmatically, so they do not take the cart
  // down with them.
  const skipCartRemovalRef = useRef(false);

  const selectionRef = useRef({});
  selectionRef.current = {
    displaysMap,
    cart,
    addDisplaysFromPolygon,
    updateArrayPolygon,
    removeDisplaysFromPolygon,
  };

  const applyMode = useCallback((nextMode) => {
    const terraDraw = terraDrawRef.current;
    if (!terraDraw) return;
    if (!terraDraw.enabled) {
      // Nothing is drawn yet, so going back to static is just bookkeeping.
      if (nextMode === STATIC_MODE) {
        setMode(STATIC_MODE);
        return;
      }
      terraDraw.start();
    }
    terraDraw.setMode(nextMode);
    setMode(nextMode);
  }, []);

  const handleFinish = useCallback((id, context) => {
    // The select mode fires `finish` too, so filter by origin mode.
    if (context.mode !== DRAW_MODE) return;
    const terraDraw = terraDrawRef.current;
    if (!terraDraw) return;
    const feature = terraDraw.getSnapshot().find((f) => f.id === id);
    const drawnPolygon = featureToPolygon(feature);
    if (!drawnPolygon) {
      terraDraw.removeFeatures([id]);
      return;
    }

    const {
      displaysMap: currentDisplays,
      cart: currentCart,
      addDisplaysFromPolygon: addToCart,
      updateArrayPolygon: updatePolygon,
    } = selectionRef.current;
    const selected = collectDisplaysInside(
      drawnPolygon,
      currentDisplays,
      currentCart
    );
    areasRef.current.set(id, { polygon: drawnPolygon, displays: selected });
    addToCart(selected);
    updatePolygon(selected);
  }, []);

  // A deleted area takes its screens out of the cart, minus the ones another
  // area still encloses.
  const handleAreaDeleted = useCallback((id) => {
    const area = areasRef.current.get(id);
    if (!area) return;
    areasRef.current.delete(id);
    if (skipCartRemovalRef.current) return;

    const remaining = Array.from(areasRef.current.values());
    const displayIds = area.displays
      .filter(
        (display) =>
          !remaining.some((other) => isInsidePolygon(display, other.polygon))
      )
      .map((display) => display.id);
    selectionRef.current.removeDisplaysFromPolygon(displayIds);
  }, []);

  const handleChange = useCallback(
    (ids, changeType) => {
      if (changeType !== "delete") return;
      ids.forEach(handleAreaDeleted);
    },
    [handleAreaDeleted]
  );

  useEffect(() => {
    const rawMap = map && map.context && map.context[MAP];
    if (!rawMap) return undefined;

    const terraDraw = createTerraDraw(rawMap);
    terraDraw.on("finish", handleFinish);
    terraDraw.on("change", handleChange);
    terraDraw.on("select", setSelectedId);
    terraDraw.on("deselect", () => setSelectedId(null));
    terraDrawRef.current = terraDraw;

    const controlDiv = document.createElement("div");
    const controls = rawMap.controls[google.maps.ControlPosition.BOTTOM_CENTER];
    controls.push(controlDiv);
    setContainer(controlDiv);

    return () => {
      terraDraw.stop();
      terraDrawRef.current = null;
      areasRef.current = new Map();
      const index = controls.getArray().indexOf(controlDiv);
      if (index > -1) controls.removeAt(index);
      setContainer(null);
    };
  }, [map, handleFinish, handleChange]);

  // Deleting the whole selection from the cart also wipes the drawn areas.
  useEffect(() => {
    if (!shouldClearPolygons) return;
    const terraDraw = terraDrawRef.current;
    if (terraDraw && terraDraw.enabled) {
      skipCartRemovalRef.current = true;
      terraDraw.clear();
      areasRef.current = new Map();
      skipCartRemovalRef.current = false;
      applyMode(STATIC_MODE);
    }
    onPolygonsCleared();
  }, [shouldClearPolygons, onPolygonsCleared, applyMode]);

  // With the buttons gone there is no way out of either interactive mode.
  useEffect(() => {
    if (hidden && mode !== STATIC_MODE) applyMode(STATIC_MODE);
  }, [hidden, mode, applyMode]);

  const toggleMode = (nextMode) =>
    applyMode(mode === nextMode ? STATIC_MODE : nextMode);

  const hasSelection = selectedId !== null;

  // Manual way out of a selection, for when `Supr` is not at hand.
  const deleteSelectedArea = () => {
    terraDrawRef.current.removeFeatures([selectedId]);
  };

  // Buttons are joined inside a single rounded shell, so each one drops its own
  // radius, border and shadow.
  const groupStyle = {
    margin: 10,
    display: "flex",
    borderRadius: 4,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
  };

  const buttonStyle = (active) => ({
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    background: active ? Colors.primary : "#ffffff",
    color: active ? "#ffffff" : "#333333",
  });

  if (!container || hidden) return null;

  return ReactDOM.createPortal(
    <div style={{ display: "flex", alignItems: "center" }}>
      <ButtonGroup style={groupStyle}>
        <Tooltip title={t("Draw area to add screens")}>
          <Button
            onClick={() => toggleMode(DRAW_MODE)}
            style={buttonStyle(mode === DRAW_MODE)}
          >
            <FontAwesomeIcon icon={faDrawPolygon} />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup style={groupStyle}>
        <Tooltip title={t("Select area")}>
          <Button
            onClick={() => toggleMode(SELECT_MODE)}
            style={buttonStyle(mode === SELECT_MODE)}
          >
            <FontAwesomeIcon icon={faMousePointer} />
          </Button>
        </Tooltip>
        <Tooltip title={t("Delete area")}>
          <Button
            onClick={deleteSelectedArea}
            disabled={!hasSelection}
            style={{
              ...buttonStyle(false),
              borderLeft: `1px solid ${Colors.border}`,
              // The inline background beats antd's disabled palette, so the
              // disabled state has to be spelled out here.
              color: hasSelection ? "#333333" : "#bfbfbf",
            }}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </Button>
        </Tooltip>
      </ButtonGroup>
    </div>,
    container
  );
};

export default DrawControlButton;
